import type { DataTableColumn, DataTableColumnApiId } from '@/components/UI/DataTableV2/DataTable.model';
import { type FC } from 'react';
import { DataTableColumnTypeIcon } from '@/components/UI/DataTableV2/headers/DataTableColumnTypeIcon';
import {
  DOSE_COLUMN_LABELS,
  DOSING_SESSION_GROUP_LABEL,
  type DoseColumnType,
} from '@/components/UI/DataTableV2/headers/doseColumnLabels';
import { isChildColumn } from '@/components/UI/DataTableV2/DataTable.util';
import styles from '@/components/UI/DataTableV2/DataTableV2.module.scss';
import { _isNotBlank, _notNil } from '@/littledash';
import { DateUtils } from '@/utils/Date.utils';
import { isIsoDurationString } from '@/helpers';
import { formatDuration } from 'date-fns';
import { parse as parseDuration } from 'tinyduration';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError';

interface ColumnNameProps {
  column: DataTableColumn;
  columnsById: Map<DataTableColumnApiId, DataTableColumn>;
}

const isDoseColumnType = (type: DataTableColumn['type']): type is DoseColumnType => type in DOSE_COLUMN_LABELS;

const doseColumnDisplayLabel = (type: DoseColumnType, unit?: string | null): string => {
  const unitLabel = type === 'doseVolume' && _notNil(unit) ? ` (${unit})` : '';
  return `${DOSE_COLUMN_LABELS[type]}${unitLabel}`;
};

/**
 * Plain-text equivalent of the rendered option label, used by react-select for filtering.
 */
export const dataTableColumnOptionLabel = (
  column: DataTableColumn,
  columnsById: Map<DataTableColumnApiId, DataTableColumn>
): string => {
  if (isChildColumn(column) && isDoseColumnType(column.type)) {
    const parentColumn = columnsById.get(column.parent_api_id);
    if (_notNil(parentColumn) && parentColumn.type === 'dosingSession') {
      const sessionTitle = _isNotBlank(parentColumn.name) ? ` · ${parentColumn.name}` : '';
      const unit = column.type === 'doseVolume' ? column.unit : undefined;
      return `${DOSING_SESSION_GROUP_LABEL}${sessionTitle} ${doseColumnDisplayLabel(column.type, unit)}`;
    }
  }
  return column.name ?? column.id;
};

/**
 * Renders a formatted column name with type-specific layouts for dropdowns and selects.
 *
 * Supports seven formatting scenarios:
 * - Sample columns: Two-line layout with sample type name, then group + timepoint
 * - Child columns: Two-line layout with parent sample info + timepoint, then column name + unit
 * - Dosing child columns: Two-line layout with Dosing · session title, then the dosing display label
 * - Measurement columns with measured_at: Two-line layout with date, then column name + unit
 * - Observation columns with observed_at: Two-line layout with date, then column name
 * - ISO duration number columns: Single-line with formatted duration
 * - Default: Single-line with column name
 */
export const ColumnName: FC<ColumnNameProps> = ({ column, columnsById }) => {
  if (isChildColumn(column)) {
    const parentColumn = columnsById.get(column.parent_api_id);
    if (_notNil(parentColumn) && parentColumn.type === 'sample' && _notNil(parentColumn.sample)) {
      const timepoint = _isNotBlank(parentColumn.sample.planned_sample_timepoint)
        ? DateUtils.renderDuration(parentColumn.sample.planned_sample_timepoint, {
            format: 'short',
            zeros: true,
            defaultResponse: '',
          })
        : '';

      return (
        <div className="flex flex-row items-center">
          <div className={styles.columnTypeSymbol}>
            <DataTableColumnTypeIcon type={column.type} />
          </div>
          <div className={styles.groupHeaderTextContent}>
            <div className={styles.groupHeaderLabel}>
              {parentColumn.sample.sample_type_name}
              {_isNotBlank(timepoint) && <> &middot; {timepoint}</>}
            </div>
            <div className={styles.groupHeaderTitle}>
              {column.name}
              {column.type === 'sampleDetail' && _notNil(column.unit) && (
                <span className={styles.columnUnit}> ({column.unit})</span>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (_notNil(parentColumn) && parentColumn.type === 'dosingSession' && isDoseColumnType(column.type)) {
      const sessionTitle = _isNotBlank(parentColumn.name) ? parentColumn.name : '';
      return (
        <div className="flex flex-row items-center">
          <div className={styles.columnTypeSymbol}>
            <DataTableColumnTypeIcon type={column.type} />
          </div>
          <div className={styles.groupHeaderTextContent}>
            <div className={styles.groupHeaderLabel}>
              {_isNotBlank(sessionTitle)
                ? `${DOSING_SESSION_GROUP_LABEL} · ${sessionTitle}`
                : DOSING_SESSION_GROUP_LABEL}
            </div>
            <div className={styles.groupHeaderTitle}>
              {DOSE_COLUMN_LABELS[column.type]}
              {column.type === 'doseVolume' && _notNil(column.unit) && (
                <span className={styles.columnUnit}> ({column.unit})</span>
              )}
            </div>
          </div>
        </div>
      );
    }
  }

  switch (column.type) {
    case 'sample':
      return (
        <div className="flex flex-row items-center">
          <div className={styles.columnTypeSymbol}>
            <DataTableColumnTypeIcon type={column.type} />
          </div>
          <div className={styles.groupHeaderTextContent}>
            <div className={styles.groupHeaderLabel}>{column.sample.sample_type_name}</div>
            <div className={styles.groupHeaderTitle}>
              <span>{column.sample.planned_sample_group}</span>
              {_isNotBlank(column.sample.planned_sample_timepoint) ? (
                <span className={`${styles.label} ml2`}>
                  {DateUtils.renderDuration(column.sample.planned_sample_timepoint, {
                    format: 'short',
                    zeros: true,
                    defaultResponse: '',
                  })}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      );

    case 'measurement':
      if (_notNil(column.measurement?.measured_at)) {
        return (
          <div className="flex flex-row items-center">
            <div className={styles.columnTypeSymbol}>
              <DataTableColumnTypeIcon type={column.type} />
            </div>
            <div className={styles.groupHeaderTextContent}>
              <div className={styles.groupHeaderLabel}>
                {DateUtils.renderDate(column.measurement.measured_at, { defaultResponse: '' })}
              </div>
              <div className={styles.groupHeaderTitle}>
                {column.name}
                {_notNil(column.unit) && <span className={styles.columnUnit}> ({column.unit})</span>}
              </div>
            </div>
          </div>
        );
      }
      break;

    case 'observation':
      if (_notNil(column.observation?.observed_at)) {
        return (
          <div className="flex flex-row items-center">
            <div className={styles.columnTypeSymbol}>
              <DataTableColumnTypeIcon type={column.type} />
            </div>
            <div className={styles.groupHeaderTextContent}>
              <div className={styles.groupHeaderLabel}>
                {DateUtils.renderDate(column.observation.observed_at, { defaultResponse: '' })}
              </div>
              <div className={styles.groupHeaderTitle}>{column.name}</div>
            </div>
          </div>
        );
      }
      break;

    case 'number':
      if (isIsoDurationString(column.name)) {
        let formattedLabel: string | null = null;
        try {
          const duration = parseDuration(column.name);
          formattedLabel = Object.values(duration).every((v) => v === 0)
            ? '0'
            : (duration.negative === true ? '- ' : '') + formatDuration(duration, { zero: false });
        } catch (e) {
          ExceptionHandler.captureException(
            new InVivoError('Failed to parse ISO duration string for column display', {
              cause: e,
              slug: 'column-name-duration-parse',
              level: 'warning',
              context: {
                columnId: column.id,
                columnName: column.name,
                columnType: column.type,
              },
            })
          );
        }

        if (_notNil(formattedLabel)) {
          return (
            <div className="flex flex-row items-center">
              <div className={styles.columnTypeSymbol}>
                <DataTableColumnTypeIcon type={column.type} />
              </div>
              <span className={`${styles.groupHeaderTitle} ml2`}>{formattedLabel}</span>
            </div>
          );
        }
      }
      break;
  }

  return (
    <div className="flex flex-row items-center">
      <div className={styles.columnTypeSymbol}>
        <DataTableColumnTypeIcon type={column.type} />
      </div>
      <span className={`${styles.groupHeaderTitle} ml2`}>{column.name}</span>
    </div>
  );
};
